export type CrazyGamesAdCallbacks = {
  adStarted: () => void;
  adFinished: () => void;
  adError: (error: { code?: string; message?: string }) => void;
};

export type CrazyGamesSettings = {
  muteAudio: boolean;
};

export type CrazyGamesSettingsListener = (settings: CrazyGamesSettings) => void;

/** localStorage-equivalent Data Module surface. `clear()` is optional and must not be used for a park reset. */
export type CrazyGamesDataModule = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear?(): void;
};

export type CrazyGamesSdkLike = {
  init(): Promise<void>;
  environment?: string;
  game: {
    gameplayStart(): void;
    gameplayStop(): void;
    settings?: CrazyGamesSettings;
    addSettingsChangeListener?(listener: CrazyGamesSettingsListener): void;
    removeSettingsChangeListener?(listener: CrazyGamesSettingsListener): void;
  };
  ad: {
    requestAd(type: string, callbacks: CrazyGamesAdCallbacks): void;
  };
  data?: CrazyGamesDataModule;
};

export type CrazyGamesInitStatus = {
  initialized: boolean;
  usable: boolean;
  reason?: 'missing' | 'disabled' | 'init_failed';
  environment?: string;
};

type CrazyGamesHost = {
  CrazyGames?: { SDK?: CrazyGamesSdkLike };
};

const callLog: string[] = [];
let initPromise: Promise<CrazyGamesInitStatus> | null = null;
let lastStatus: CrazyGamesInitStatus = { initialized: false, usable: false, reason: 'missing' };
let injectedSdk: CrazyGamesSdkLike | null | undefined;

export function crazyGamesSdkCallLog(): readonly string[] {
  return callLog;
}

export function resetCrazyGamesSdkForTests(): void {
  initPromise = null;
  lastStatus = { initialized: false, usable: false, reason: 'missing' };
  injectedSdk = undefined;
  callLog.length = 0;
}

export function setCrazyGamesSdkForTests(sdk: CrazyGamesSdkLike | null): void {
  resetCrazyGamesSdkForTests();
  injectedSdk = sdk;
}

function resolveSdk(): CrazyGamesSdkLike | null {
  if (injectedSdk !== undefined) {
    return injectedSdk;
  }
  const host = globalThis as typeof globalThis & CrazyGamesHost;
  return host.CrazyGames?.SDK ?? null;
}

export function getCrazyGamesInitStatus(): CrazyGamesInitStatus {
  return lastStatus;
}

export function getInitializedCrazyGamesSdk(): CrazyGamesSdkLike | null {
  if (!lastStatus.initialized || !lastStatus.usable) {
    return null;
  }
  return resolveSdk();
}

export async function initCrazyGamesSdk(): Promise<CrazyGamesInitStatus> {
  if (initPromise) {
    return initPromise;
  }
  initPromise = (async () => {
    const sdk = resolveSdk();
    if (!sdk) {
      lastStatus = { initialized: true, usable: false, reason: 'missing' };
      callLog.push('init:missing');
      return lastStatus;
    }
    try {
      callLog.push('init');
      await sdk.init();
      const environment = sdk.environment;
      if (environment === 'disabled') {
        lastStatus = { initialized: true, usable: false, reason: 'disabled', environment };
        callLog.push('init:disabled');
        return lastStatus;
      }
      lastStatus = { initialized: true, usable: true, environment };
      return lastStatus;
    } catch {
      lastStatus = { initialized: true, usable: false, reason: 'init_failed' };
      callLog.push('init:failed');
      return lastStatus;
    }
  })();
  return initPromise;
}

export function emitGameplayStart(): void {
  if (!lastStatus.initialized || !lastStatus.usable) {
    return;
  }
  const sdk = resolveSdk();
  if (!sdk) {
    return;
  }
  callLog.push('gameplayStart');
  sdk.game.gameplayStart();
}

export function emitGameplayStop(): void {
  if (!lastStatus.initialized || !lastStatus.usable) {
    return;
  }
  const sdk = resolveSdk();
  if (!sdk) {
    return;
  }
  callLog.push('gameplayStop');
  sdk.game.gameplayStop();
}

export function requestCrazyGamesRewardedAd(callbacks: CrazyGamesAdCallbacks): void {
  if (!lastStatus.initialized) {
    callbacks.adError({ code: 'not_initialized', message: 'SDK not initialized' });
    return;
  }
  const sdk = resolveSdk();
  if (!sdk || !lastStatus.usable) {
    const code = lastStatus.reason === 'disabled' ? 'basic-launch-disabled' : 'unavailable';
    callbacks.adError({ code, message: lastStatus.reason ?? 'unavailable' });
    return;
  }
  callLog.push('requestAd:rewarded');
  sdk.ad.requestAd('rewarded', callbacks);
}
